import { useMemo } from 'react';
import { Box } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { Badge } from '../../../../components/ui/Badge';
import { formatDate } from '../../../../lib/date/formatDate';
import { DATE_FORMAT } from '../../../../lib/resolveFields';
import type { InstanceRow } from '../../instanceTypes';

interface InstanceMetaBadgesProps {
  instance: InstanceRow;
  activeKeyCount: number;
}

export function InstanceMetaBadges({ instance, activeKeyCount }: InstanceMetaBadgesProps) {
  const theme = useTheme();
  const { t } = useTranslation('instances');

  const badgeSx = useMemo(() => ({
    bgcolor: alpha(theme.palette.primary.main, 0.07),
    color: theme.palette.text.body,
    border: true,
    borderColor: alpha(theme.palette.primary.main, 0.18),
    fontWeight: theme.typography.fontWeightMedium as number,
  }), [theme]);

  const statusDotColor =
    instance.status === 'ACTIVATED' ? theme.palette.success.main :
    theme.palette.warning.main;

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
      <Badge {...badgeSx} dot={statusDotColor} dotGlow={instance.status === 'ACTIVATED'}>
        {t(`status.${instance.status}`)}
      </Badge>
      <Badge {...badgeSx}>
        {activeKeyCount} {t('detail.meta.activeKeys')}
      </Badge>
      <Badge {...badgeSx}>
        {instance.codeterrain.length} {t('detail.meta.terrains')}
      </Badge>
      <Badge {...badgeSx}>
        {t('detail.meta.createdAt')} {formatDate(instance.createdAt, DATE_FORMAT)}
      </Badge>
    </Box>
  );
}
